import React, { createContext, forwardRef, useContext, useMemo } from 'react';

import type { ListChildComponentProps, ListOnItemsRenderedProps } from 'react-window';
import { FixedSizeList } from 'react-window';

/**
 * Fixed option-row height. Rows are single-line (name, optional checkbox/avatar),
 * so a fixed height lets us use FixedSizeList — no per-row measurement. Matches
 * MUI's default Autocomplete option height so content isn't clipped.
 */
export const partyListboxRowHeight = 48;
const maxVisibleRows = 8;
/** Fetch the next page this many rows before the end so the tail never goes blank. */
const prefetchRows = 10;

interface PartyListboxPaginationContextValue {
  hasMore: boolean;
  loading: boolean;
  loadMore: () => void;
}

const PartyListboxPaginationContext = createContext<PartyListboxPaginationContextValue | null>(
  null
);

/**
 * Carries pagination state to the virtualized listbox. `slots.listbox` is a
 * component type that MUI instantiates internally, so per-instance closures
 * can't reach it directly — context is the clean bridge. Wrap the Autocomplete
 * in this and pass {@link VirtualizedPartyListbox} via `slots={{ listbox }}`.
 */
export function PartyListboxPaginationProvider({
  children,
  hasMore,
  loadMore,
  loading,
}: {
  children: React.ReactNode;
  hasMore: boolean;
  loading: boolean;
  loadMore: () => void;
}): JSX.Element {
  const value = useMemo(() => ({ hasMore, loadMore, loading }), [hasMore, loading, loadMore]);
  return (
    <PartyListboxPaginationContext.Provider value={value}>
      {children}
    </PartyListboxPaginationContext.Provider>
  );
}

/**
 * MUI applies the listbox props (className, role, id, aria-*, onKeyDown,
 * onMouseDown) to the element it hands us, but react-window scrolls its own
 * outer element — forward those props onto it so a11y + keyboard handlers work.
 */
const OuterElementContext = createContext<React.HTMLAttributes<HTMLElement>>({});

const OuterElementType = forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  function OuterElementType(props, ref) {
    const outerProps = useContext(OuterElementContext);
    return <div ref={ref} {...props} {...outerProps} />;
  }
);

type PartyOptionElement = React.ReactElement<React.HTMLAttributes<HTMLElement>>;

function renderRow({ data, index, style }: ListChildComponentProps<PartyOptionElement[]>) {
  const element = data[index];
  return React.cloneElement(element, { style: { ...element.props.style, ...style } });
}

/**
 * Virtualized Autocomplete listbox for the party pickers. Reads MUI's rendered
 * option `<li>` elements from `children`, renders only the visible window via
 * react-window, and calls `loadMore()` as the viewport nears the loaded end.
 */
export const VirtualizedPartyListbox = forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLElement>
>(function VirtualizedPartyListbox({ children, ...other }, ref) {
  const context = useContext(PartyListboxPaginationContext);
  const hasMore = context?.hasMore ?? false;
  const loading = context?.loading ?? false;
  const loadMore = context?.loadMore;

  const items = useMemo(
    () =>
      React.Children.toArray(children).filter((child): child is PartyOptionElement =>
        React.isValidElement(child)
      ),
    [children]
  );
  const itemCount = items.length;
  const height = Math.min(itemCount, maxVisibleRows) * partyListboxRowHeight;

  /**
   * Fetch the next page when the viewport nears the end. Guard on the hook's
   * `loading` (not a fired-count ref): a fetch already in flight will re-fire
   * this once it resolves and `loading` clears, so pagination can't get stuck.
   * `loadMore` is itself a no-op while loading, so redundant calls are safe.
   */
  const handleItemsRendered = ({ visibleStopIndex }: ListOnItemsRenderedProps) => {
    if (!hasMore || loading || !loadMore) return;
    if (visibleStopIndex >= itemCount - prefetchRows) loadMore();
  };

  return (
    <div ref={ref}>
      <OuterElementContext.Provider value={other}>
        <FixedSizeList
          height={height}
          innerElementType="ul"
          itemCount={itemCount}
          itemData={items}
          itemSize={partyListboxRowHeight}
          outerElementType={OuterElementType}
          overscanCount={8}
          width="100%"
          onItemsRendered={handleItemsRendered}
        >
          {renderRow}
        </FixedSizeList>
      </OuterElementContext.Provider>
    </div>
  );
});
